/**
 * The three sites.
 *
 * GOODBUILD is one product with three audiences, and section 04 of the brand
 * guidelines already treats them as separate surfaces with separate grounds.
 * This file makes that separation structural: `goodbuild.cc` is the store,
 * `creators.goodbuild.cc` is where a creator works, `developers.goodbuild.cc`
 * is where a studio works.
 *
 * **One deployment, three sites.** They are routed apart by Host header rather
 * than built apart, because all three read the same catalogue, the same
 * session and the same ledger. Three applications would mean either three
 * copies of the shared packages or a fourth extracted to hold them, and would
 * buy nothing a route namespace does not already give.
 *
 * The mapping is entirely in this file, and `src/middleware.ts` is the only
 * thing that applies it. Everything downstream — pages, chrome, canonical URLs
 * — asks here rather than parsing hostnames itself.
 */

export type SiteId = 'store' | 'creators' | 'developers';

export interface Site {
  readonly id: SiteId;
  /** The production host this site answers on. */
  readonly host: string;
  /**
   * Where this site's routes physically live under `src/app`.
   *
   * Empty for the store, whose pages sit in the `(store)` route group and so
   * keep their URLs unchanged. The other two are real path segments that the
   * middleware rewrites into, which is why they cannot be route groups: a
   * group contributes nothing to the URL and there would be nothing to rewrite
   * to.
   */
  readonly routePrefix: string;
  /**
   * Port `pnpm dev` answers this site on locally.
   *
   * **All three are the same number now, and that is the truth rather than a
   * mistake.** They were 3000, 3001 and 3002 — the three ports one Next process
   * served three hosts on — and then 4321 and two 5173s, when the storefront
   * was a separate Astro app. There is one Vite dev server on 5173 serving
   * every path on every host, so there is one port to name.
   *
   * Kept as a field rather than collapsed to a constant because it is a
   * property of a site, and the day one of them is served separately again this
   * is where that is said.
   */
  readonly devPort: number;
  /** What this site is called in its own chrome. */
  readonly name: string;
  /** Shown in the site switcher on the other two sites. */
  readonly switcherLabel: string;
}

export const SITES: Readonly<Record<SiteId, Site>> = {
  store: {
    id: 'store',
    host: 'goodbuild.cc',
    routePrefix: '',
    devPort: 5173,
    name: 'GOODBUILD',
    switcherLabel: 'Store',
  },
  creators: {
    id: 'creators',
    host: 'creators.goodbuild.cc',
    routePrefix: '/creators',
    devPort: 5173,
    name: 'GOODBUILD Creators',
    switcherLabel: 'Creators',
  },
  developers: {
    id: 'developers',
    host: 'developers.goodbuild.cc',
    routePrefix: '/developers',
    devPort: 5173,
    name: 'GOODBUILD Developers',
    switcherLabel: 'Developers',
  },
} as const;

export const SITE_IDS: readonly SiteId[] = ['store', 'creators', 'developers'];

export function isSiteId(value: unknown): value is SiteId {
  return typeof value === 'string' && (SITE_IDS as readonly string[]).includes(value);
}

/**
 * Paths that mean the same thing on every host and are never rewritten.
 *
 * Authentication is here because a creator hitting a protected page on
 * `creators.goodbuild.cc` must be able to sign in without being thrown across
 * to another domain mid-flow — which is also why sign-in is a page we serve
 * rather than a hosted portal somewhere else. The API and the webhooks are here
 * because a Stripe callback carries a signature and no opinion about which of
 * our sites it arrived at.
 */
export const SHARED_PREFIXES: readonly string[] = [
  '/api',
  '/sign-in',
  '/sign-up',
  '/monitoring',
  '/_next',
  '/favicon.ico',
  '/robots.txt',
  '/sitemap.xml',
];

export function isSharedPath(pathname: string): boolean {
  return SHARED_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/**
 * Which site a Host header belongs to.
 *
 * Unknown hosts resolve to the store rather than throwing: preview
 * deployments, health checks and anything hitting the origin by IP all arrive
 * with a host nobody registered, and the storefront is the right thing to show
 * a stranger.
 *
 * `creators.localhost` and `developers.localhost` resolve too, so the split can
 * be exercised in a browser without a hosts file — the `.localhost` TLD is
 * reserved to the loopback address and Chrome and Firefox both honour it.
 */
export function resolveSite(host: string | null | undefined): SiteId {
  if (!host) return 'store';

  // Strip the port, lowercase, and drop a leading `www.` so the canonical and
  // the www form of the store never disagree.
  const name = host
    .split(':')[0]!
    .toLowerCase()
    .replace(/^www\./, '');
  const [first] = name.split('.');

  for (const id of SITE_IDS) {
    if (id === 'store') continue;
    const site = SITES[id];
    if (name === site.host || first === id) return id;
  }

  return 'store';
}

/**
 * The internal App Router path for a public path on a given site.
 *
 * `creators.goodbuild.cc/board` is served by `src/app/creators/board`, and the
 * root of a subsite maps to the prefix itself rather than to `/creators/`,
 * because a trailing slash is a different route to Next.
 */
export function internalPath(site: SiteId, pathname: string): string {
  const prefix = SITES[site].routePrefix;
  if (prefix === '') return pathname;
  return pathname === '/' ? prefix : `${prefix}${pathname}`;
}

/** The public path for an internal one. The inverse of `internalPath`. */
export function publicPath(site: SiteId, internal: string): string {
  const prefix = SITES[site].routePrefix;
  if (prefix === '' || !internal.startsWith(prefix)) return internal;
  return internal.slice(prefix.length) || '/';
}

/**
 * An absolute URL on one of the sites.
 *
 * Cross-site links have to be absolute — a `<Link href="/board">` rendered in
 * the store's footer would land on `goodbuild.cc/board`, which is not a route
 * and never will be. In development the sites are ports rather than
 * subdomains, so the origin is built from `devPort` instead.
 */
export function siteUrl(site: SiteId, path = '/'): string {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${siteOrigin(site)}${suffix === '/' ? '' : suffix}`;
}

/**
 * The origin of one of the sites.
 *
 * **Not for links a person clicks, and there are none left.** The chrome, the
 * pitch pages and the store capsules all went through here once, and every one
 * of them was wrong in a dev session: this function has no idea which servers
 * are running, so it guessed from `NODE_ENV` and a table of ports. Every one of
 * those surfaces is served by one deployment now and writes a relative path.
 *
 * What is left is the one caller that has no browser to ask: the Stripe
 * Connect onboarding links, which were absolute URLs handed to Stripe to
 * redirect back to. Both the links and the file that built them went with the
 * provider; the requirement they justified is still the one below.
 *
 * Keyed off `NODE_ENV` because this is evaluated in a Worker, in a script and
 * potentially in a bundle, and that is the one value all three inline the same
 * way. Only `development` maps to ports; tests get the production hosts, which
 * is what makes the assertions in `test/sites.test.ts` mean something.
 */
export function siteOrigin(site: SiteId): string {
  if (process.env.NODE_ENV === 'development') return `http://localhost:${SITES[site].devPort}`;
  return `https://${SITES[site].host}`;
}

/**
 * Store paths that used to serve a page which now lives on a subdomain.
 *
 * Kept as permanent redirects rather than deleted. These URLs are in the wild
 * — a creator has the board bookmarked, a studio has the portal bookmarked —
 * and a 404 on a bookmark reads as the platform having removed the thing.
 */
export const MOVED_FROM_STORE: readonly {
  from: string;
  site: SiteId;
  to: string;
}[] = [
  { from: '/creators', site: 'creators', to: '/' },
  { from: '/creator/store', site: 'creators', to: '/board' },
  { from: '/developers', site: 'developers', to: '/' },
  { from: '/portal', site: 'developers', to: '/portal' },
];

/** Where a store path moved to, or null if it did not move. */
export function movedFromStore(pathname: string): { site: SiteId; to: string } | null {
  for (const entry of MOVED_FROM_STORE) {
    if (pathname === entry.from) return { site: entry.site, to: entry.to };
    if (pathname.startsWith(`${entry.from}/`)) {
      return {
        site: entry.site,
        to: `${entry.to === '/' ? '' : entry.to}${pathname.slice(entry.from.length)}`,
      };
    }
  }
  return null;
}

/**
 * The signed-in surface: paths that belong to an account rather than to the shop.
 *
 * **This list was born a deployment map and is a session map now.** It existed
 * because the storefront and the dashboards were two Workers on two hosts, and
 * the shared chrome held relative paths that were correct on one of them and a
 * 404 on the other — `Sign in` in the store's header and `Your library` in its
 * footer were both dead on every public page. There is one deployment again, so
 * that job is done — `chromeHref` in `apps/app/src/ui` was the resolver that read
 * this list, and it went with the second origin it existed to resolve against.
 * Every path in the chrome is relative and correct against whatever host
 * answered, which is what a single-origin deployment wants.
 *
 * What the list still answers, and what it is maintained for, is **whether a
 * path needs a session**. `needsSession` in the app's router is the one caller
 * that matters: everything here is behind the sign-in gate, everything not here
 * — the catalogue, a store page, the pitch pages, the legal documents — is
 * public. A page added to the router needs no entry here; a page added *here*
 * is put behind a wall, which is the right way round for a list that can be
 * forgotten.
 *
 * **The five authentication paths are the documented exception.** They are this
 * application's rather than the shop's and belong on the list for that reason,
 * and they cannot be gated: a sign-in page behind a sign-in is the one page
 * that must never require one. The router names them in `AUTH_PATHS` and
 * subtracts them.
 *
 * Nothing else asks any more. `chromeHref` in `apps/app/src/ui` was the other
 * caller and went with the second origin it existed to resolve against.
 */
export const APP_PATHS: readonly string[] = [
  /*
   * The six that are on this list and are not behind the gate. See the note
   * above, and `AUTH_PATHS` in `apps/app/src/router.tsx`, which is the list
   * that subtracts them.
   */
  '/sign-in',
  '/sign-up',
  '/forgot-password',
  '/reset-password',
  '/verified',
  /*
   * The wall somebody waits behind until they have confirmed their address.
   * Exempt from the gate like the five above it — it is a page about a session
   * rather than a page that reads one — and listed here so the router's sweep
   * can see that the exemption is deliberate.
   */
  '/verify-email',
  '/library',
  '/orders',
  /*
   * The launcher's own screen, and it is one character from a public page.
   *
   * `/downloads`, plural, lists what the desktop launcher is installing and is
   * registered as a route only inside it. `/download`, singular, is the public
   * page that offers the launcher to somebody who has none — so it is
   * deliberately *not* on this list, and putting it here would put the one page
   * that hands out the installer behind a sign-in.
   */
  '/downloads',
  /*
   * Missed once when the account page was added, and the cost of missing one
   * has changed rather than gone away. It used to send a signed-in person's own
   * account page to the other origin; now it leaves a screen about somebody's
   * account, orders and machines readable without a session. That is the worse
   * of the two failures, which is why `apps/app/test/account.test.ts` sweeps
   * the router against this list.
   */
  '/account',
  '/bought',
  // Identity and business verification. Not in `AUDIENCE_PATHS` below and that
  // is deliberate: like `/account` and `/onboarding`, it belongs to whoever is
  // signed in rather than to one side of the product — a studio reaches it to
  // sign a publishing agreement and a creator will reach it to be paid, and
  // painting it in either side's colours would be wrong for the other.
  '/verification',
  '/board',
  '/portal',
  '/onboarding',
  '/admin',
  '/settings',
];

/** Whether this path belongs to the signed-in surface. See `APP_PATHS`. */
export function isAppPath(pathname: string): boolean {
  return APP_PATHS.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/**
 * Which audience a path in the signed-in app belongs to.
 *
 * The three sites are three audiences, and `resolveSite` answers that question
 * from the hostname — which is the right answer for the storefront and is no
 * answer at all for the React app. That app is one deployment on
 * `app.goodbuild.cc`, so `resolveSite` returns `store` for every page it
 * serves: the developer portal, the opportunity board and a player's library
 * all came out in the store's ground and the store's accent, and a studio
 * standing in its own portal was looking at the players' colours.
 *
 * So the audience of a page in that app comes from its path rather than from
 * its host. `/portal` is a studio's workplace wherever it is served from, and
 * `/board` is a creator's, and neither fact depends on which of our hostnames
 * somebody typed.
 *
 * **Null rather than a guess.** `/account`, `/onboarding`, `/admin` and the
 * sign-in pages belong to no single audience: an account page belongs to
 * whoever is signed in, onboarding belongs to whichever application it is
 * showing, and the admin panel is staff. Those decide for themselves — the
 * caller falls back to the host, or states the space outright — and a lookup
 * that answered `store` for them would be indistinguishable from one that had
 * been asked about a player's page.
 */
export const AUDIENCE_PATHS: Readonly<Record<SiteId, readonly string[]>> = {
  /*
   * `/publishing` was here beside `/portal`, and #142 removed it with the page.
   *
   * It was the one entry on this side not behind a session: section 04 puts one
   * channel per surface, and the pitch page was where a studio first met the
   * product, so it took Build blue and a reader following it to the portal did
   * not change colour on the way. There is no pitch page, and a prefix listed
   * for a path nothing serves is a colour rule waiting to be applied to
   * whatever claims the name next.
   *
   * The creators' side keeps `/creators`, which is the same shape and is still
   * a page: that programme is open and unaffected.
   */
  developers: ['/portal'],
  creators: ['/board', '/creators', '/c'],
  /*
   * `/downloads` and `/settings/launcher` are the launcher's two screens, and
   * they are a player's surface wherever they are opened — so they take the
   * store's ground and the store's accent, exactly like the library they sit
   * beside. `/settings` is named with its child rather than whole, because
   * nothing has claimed the prefix and a later settings page for studios must
   * be free to be theirs.
   */
  store: [
    '/library',
    '/orders',
    '/bought',
    '/downloads',
    '/settings/launcher',
    /*
     * The public shop, which is a player's surface whether or not there is a
     * player signed in.
     *
     * Stated rather than left to the fallback, because the fallback is the
     * *host* — and one deployment answers on three of them. Without these,
     * `/browse` read on `developers.goodbuild.cc` would come out in Build blue,
     * which is section 04 failing in the one direction it exists to prevent: a
     * page that tells you which side of the product you are on, incorrectly.
     */
    '/browse',
    '/g',
    '/dev',
    '/pub',
    '/about',
    '/contact',
    '/download',
    '/legal',
  ],
} as const;

/** The audience a path belongs to, or null when the path does not say. */
export function audienceForPath(pathname: string): SiteId | null {
  for (const id of SITE_IDS) {
    const owned = AUDIENCE_PATHS[id].some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );
    if (owned) return id;
  }
  return null;
}

/**
 * Routes that require a signed-in user, stated as internal paths.
 *
 * Matched after the host has been resolved, so `/board` on the creators host is
 * gated by its internal path `/creators/board` and the same `/board` on the
 * store host is simply not a route. Matching the public path instead would mean
 * every site's namespace had to avoid colliding with every other site's
 * protected paths, which is a rule nobody would remember.
 */
export const PROTECTED_INTERNAL_PREFIXES: readonly string[] = [
  '/library',
  '/orders',
  '/settings',
  '/admin',
  '/creators/board',
  '/developers/portal',
];

export function isProtectedInternalPath(internal: string): boolean {
  return PROTECTED_INTERNAL_PREFIXES.some(
    (prefix) => internal === prefix || internal.startsWith(`${prefix}/`),
  );
}
